import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

// region Imports - External libraries
import _ from "lodash";
import * as dateFns from "date-fns";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Organisms
import GenericQueryFilter, {
  IDateRangeOptions,
  IMultipleSelectionOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";
// endregion Imports - Organisms
// region Imports - Shared
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { IntegrationLogListItemResponseDto } from "@shared/dtos/integration-logs/integration-log-list-item-response.dto";
// endregion Imports - Shared
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Utils
import utils from "@utils/useful-functions";
import { vehicleFilterLabel } from "@utils/vehicle-filter-label";
// endregion Imports - Utils

interface IProps {
  open: boolean;
  onClose: () => void;
  onFilter: (
    numberOfFilteredOptions: number,
    logsData: IntegrationLogListItemResponseDto[],
    startDate: string,
    finishDate: string
  ) => void;
}

const QueryFilterIntegrationsReport: React.FC<IProps> = ({ open, onClose, onFilter }) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region States
  const [optionsVehicleFilter, setOptionsVehicleFilter] = useState<Vehicle[]>([] as Array<Vehicle>);
  const [loadingVehicleFilter, setLoadingVehicleFilter] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  // endregion States
  // region Constants
  const paramNameStartDate = "startDate";
  const paramNameEndDate = "finishDate";
  const paramNameVehicle = "idVehicles";

  const vehicleFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleFilter,
    labelName: t(FilterMessages.optionVehicles),
    paramName: paramNameVehicle,
    values: optionsVehicleFilter.map((vehicle) => ({ value: vehicle.id_vehicle, label: vehicleFilterLabel(vehicle) }))
  }), [loadingVehicleFilter, optionsVehicleFilter, t]);

  const defaultStartDate = dateFns.startOfDay(dateFns.subDays(new Date(), 1));
  const defaultEndDate = dateFns.endOfDay(new Date());

  const dateRangeOptions: IDateRangeOptions = {
    labelName: t(FilterMessages.dateRangeLabel),
    paramNameStartDate,
    paramNameEndDate,
    defaultValues: {
      startDate: utils.convertDateToISOWithTimezone(defaultStartDate),
      endDate: utils.convertDateToISOWithTimezone(defaultEndDate)
    },
    maxRange: "7D",
    maxPeriod: "30D"
  };

  const initialParamsFilter: IParamQueryValues[] = [
    { paramName: paramNameStartDate, paramValue: utils.convertDateToISOWithTimezone(defaultStartDate) },
    { paramName: paramNameEndDate, paramValue: utils.convertDateToISOWithTimezone(defaultEndDate) }
  ];
  // endregion Constants
  // region Refs
  const paramsFilterRef = useRef<IParamQueryValues[]>(initialParamsFilter);
  // endregion Refs
  // region Functions

  /**
   * Asynchronously loads vehicle options from the server and updates the application state accordingly.
   *
   * This function fetches all vehicle options from the server, applying specific parameters to the request
   * for linked hardware and including only labels. Depending on the response status, it either updates the
   * vehicle filter options in the state or displays a toast notification with the appropriate message.
   *
   * In case of an error during the request, it shows an error toast with information about the failure.
   * After execution, it ensures that the loading state for the vehicle filter options is set to false.
   *
   * @async
   * @function loadVehicleOptions
   */
  const loadVehicleOptions = useCallback(async () => {
    try {
      // Get all vehicles
      const { data } = await api.get("vehicles/read", { params: { linkedHardware: true, onlyLabel: true } });

      if (data.status === "success") setOptionsVehicleFilter(data.result);
      else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoadingVehicleFilter(false);
    }
  }, [addToast, t]); // Dependencies for useCallback

  /**
   * Asynchronously handles filtering operations for logs by processing query parameters,
   * triggering API requests, and updating the application's state accordingly.
   *
   * @param {IParamQueryValues[]} paramsData - The array of query parameters containing parameter names and their respective values.
   *
   * @returns {Promise<IOnFilterReturn>} A promise resolving with the result status of the filtering operation.
   */
  const handleFilter = useCallback(async (paramsData: IParamQueryValues[]): Promise<IOnFilterReturn> => {
    setLoadingLogs(true);
    paramsFilterRef.current = paramsData;

    try {
      const mappedParams = { ...paramsData.reduce((acc, param) => {
        acc[param.paramName] = param.paramValue;

        return acc;
      }, {} as { [key: string]: string | string[] }) };
      const { data } = await api.post("integration-logs/read", { ...mappedParams });

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));
      const validParamsDataWithoutDate = validParamsData.filter(
        (item) => item.paramName !== paramNameStartDate && item.paramName !== paramNameEndDate
      );

      if (data.status !== "success") addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

      const startDateValue = validParamsData.find((item) => item.paramName === paramNameStartDate)?.paramValue as string;
      const finishDateValue = validParamsData.find((item) => item.paramName === paramNameEndDate)?.paramValue as string;

      onFilter(
        validParamsDataWithoutDate.length,
        data.result ?? [],
        startDateValue,
        finishDateValue
      );

      return { status: "success" } as IOnFilterReturn;
    } catch (error) {
      utils.handleStandardError(error, t, addToast);

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingLogs(false);
    }
  }, [addToast, onFilter, t]); // Dependencies for useCallback

  // endregion Functions
  // region Effects

  // Load options from the API
  useEffect(() => {
    loadVehicleOptions().then();
  }, [loadVehicleOptions]);

  // endregion Effects

  return (
    <>
      <Loading loading={loadingLogs} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.INTEGRATION_REPORT}
        onFilter={(paramsData) => handleFilter(paramsData)}
        multipleSelectionOptions={[
          vehicleFilterOptions
        ]}
        dateRangeOptions={dateRangeOptions}
      />
    </>
  );
};

export default QueryFilterIntegrationsReport;
